import React from 'react';
import ReactDOM from 'react-dom/client';
import '@stt-componentes/core-v3/styles.css';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { initFirebase, getFcmToken } from './firebase';
import { getMessaging, onMessage } from 'firebase/messaging';
import { initConectividade } from './signals/conectividade';
import { atualizacaoDisponivel } from './signals/atualizacao';

const initializeApp = (config) => {
    global.gConfig = config;

    initConectividade();

    const rootElement = document.getElementById('root');
    
    // Previne criação de múltiplas roots no mesmo elemento
    if (!rootElement._reactRootContainer) {
        const root = ReactDOM.createRoot(rootElement);
        root.render(<App />);
    }
};

const verificarBaseAlternativa = (config) => {
    let subpaths = window.location.pathname;
    if (subpaths && config.layoutsAlternativos) {
        subpaths = subpaths.split('/');

        let layoutAlternativo = null;
        subpaths.forEach((sp) => {
            if (config.layoutsAlternativos.includes(sp)) {
                layoutAlternativo = sp;
                return true;
            }
        });

        if (layoutAlternativo) {
            return fetch(`${process.env.PUBLIC_URL}/config/layoutsAlternativos/${layoutAlternativo}/config.stt-${config.config_id}.json`, {
                headers: { 'Content-Type': 'application/json' },
            }).then((response) => response.json());
        }
    }
    return config;
};

// Previne execução múltipla do código de inicialização
// Verifica se já existe root criada ou se já está em processo de inicialização
const rootElement = document.getElementById('root');
if (!rootElement._reactRootContainer && !window.__APP_INITIALIZING__) {
    window.__APP_INITIALIZING__ = true;
    
    Promise.all([
        fetch(`${process.env.PUBLIC_URL}/config/changelog.json`).then((response) => response.json()),
        fetch(`${process.env.PUBLIC_URL}/config/config.json`).then((response) => response.json()),
    ])
        .then(([changelog, config]) => Promise.all([changelog, verificarBaseAlternativa(config)]))
        .then(([changelog, config]) => {
            initializeApp({ ...config, ...changelog });
            window.__APP_INITIALIZING__ = false;
        })
        .catch((error) => {
            console.error('Erro ao buscar os arquivos de configurações do frontend', error);
            window.__APP_INITIALIZING__ = false;
        });
}

// ── Aviso de nova versão do PWA (modo prompt) ──────────────────────────────
// O SW novo instala e fica em `waiting` (src/sw.js não chama mais skipWaiting no topo).
// Aqui detectamos esse estado, avisamos o usuário e, ao clicar "Atualizar", o
// `controllerchange` recarrega a página. Ver docs/adr/0003-pwa-update-em-modo-prompt.md.
const CHECAGEM_ATUALIZACAO_MS = 10 * 60 * 1000; // ~10min

// Guard anti-loop: garante um único reload quando o SW novo assume o controle.
let recarregandoParaAtualizar = false;
const registrarControllerChange = () => {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (recarregandoParaAtualizar) return;
        recarregandoParaAtualizar = true;
        window.location.reload();
    });
};

// Enriquece o aviso com versão + novidades do changelog.json (fetch sem cache).
// Fallback silencioso para mensagem genérica se o fetch falhar.
const carregarChangelogAtualizacao = async () => {
    try {
        const resposta = await fetch(`${process.env.PUBLIC_URL}/config/changelog.json`, {
            cache: 'no-store',
        });
        if (!resposta.ok) return { versao: null, novidades: null };
        const changelog = await resposta.json();
        return {
            versao: changelog.modulo_versao || null,
            novidades: Array.isArray(changelog.modulo_novidades) ? changelog.modulo_novidades : null,
        };
    } catch (_) {
        return { versao: null, novidades: null };
    }
};

// Não-nag: não reabre o aviso para o mesmo worker em `waiting` (ex.: após "Depois").
// Só reabre quando um novo hash produz um novo worker.
let ultimoWaiting = null;
const anunciarAtualizacao = async (registration) => {
    const waiting = registration.waiting;
    if (!waiting || waiting === ultimoWaiting) return;
    ultimoWaiting = waiting;
    const { versao, novidades } = await carregarChangelogAtualizacao();
    atualizacaoDisponivel.value = { open: true, registration, versao, novidades };
};

const observarAtualizacoes = (registration) => {
    // Avisa quando um SW novo termina de instalar. Só em update real: `controller`
    // presente distingue update da 1ª instalação (na 1ª não há controller).
    const anunciarQuandoInstalado = (worker) => {
        if (!worker) return;
        if (worker.state === 'installed' && navigator.serviceWorker.controller) {
            anunciarAtualizacao(registration);
            return;
        }
        worker.addEventListener('statechange', () => {
            if (worker.state === 'installed' && navigator.serviceWorker.controller) {
                anunciarAtualizacao(registration);
            }
        });
    };

    // SW já em `waiting` no momento do registro (update detectado antes deste load).
    if (registration.waiting && navigator.serviceWorker.controller) {
        anunciarAtualizacao(registration);
    }

    // SW que já estava instalando no load — cobre a corrida em que o `updatefound`
    // disparou antes de anexarmos o listener abaixo (ex.: reload no instante do deploy).
    anunciarQuandoInstalado(registration.installing);

    // SW novo que passar a instalar depois deste load.
    registration.addEventListener('updatefound', () => {
        anunciarQuandoInstalado(registration.installing);
    });

    // Checagem ativa: pega deploy mesmo com a aba parada o dia todo.
    const checar = () => {
        if (document.visibilityState === 'visible') {
            registration.update().catch(() => {});
        }
    };
    document.addEventListener('visibilitychange', checar);
    setInterval(checar, CHECAGEM_ATUALIZACAO_MS);
};

if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    window.addEventListener('load', () => {
        navigator.serviceWorker
            .register('/sw.js')
            .then(async (registration) => {
                registrarControllerChange();
                observarAtualizacoes(registration);

                const firebaseConfig = global.gConfig && global.gConfig.firebase;
                if (!firebaseConfig) return;
                await initFirebase(firebaseConfig);
                const token = await getFcmToken(firebaseConfig.vapidKey);
                if (token) {
                    // TODO: enviar token para o backend para receber pushes deste device
                    console.info('FCM token registrado', token);
                }

                const messaging = getMessaging();

                onMessage(messaging, (payload) => {
                    console.log('Mensagem recebida em foreground: ', payload);

                    // Opcional: Mostrar notificação nativa do navegador
                    const { title, body, icon } = payload.notification;

                    // Verifica se temos permissão
                    if (Notification.permission === 'granted') {
                        new Notification(title, {
                            body: body,
                            icon: icon || '/icon-192.png',
                        });
                    }
                });
            })
            .catch((err) => {
                console.error('Falha ao registrar service worker', err);
            });
    });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
